import { z } from 'zod';

/**
 * The admin dashboard's numbers. "Sales" are confirmed orders: placed, and not cancelled, failed, refunded in full or still
 * waiting for their payment. Revenue is what they came to minus anything refunded. Money is in minor units (paise).
 */

export const DASHBOARD_RANGES = [7, 30, 90, 365] as const;
export type DashboardRange = (typeof DASHBOARD_RANGES)[number];

export const dashboardRangeSchema = z.object({
  range: z.coerce
    .number()
    .int()
    .refine((v): v is DashboardRange => (DASHBOARD_RANGES as readonly number[]).includes(v), 'Choose 7, 30, 90 or 365 days')
    .default(30),
});

/** A number for this period beside the one for the period before it. */
export interface Compared {
  current: number;
  previous: number;
  /** percent change, or null when there is nothing before to compare with (shown as "new") */
  changePct: number | null;
}

export interface DayPoint {
  /** the day in India time, "2026-10-19" */
  date: string;
  revenue: number;
  orders: number;
}

export interface StateSales {
  /** the two-digit GST state code, which is what the map keys on */
  code: string;
  name: string;
  orders: number;
  revenue: number;
  customers: number;
}

export interface CitySales {
  city: string;
  state: string;
  orders: number;
  revenue: number;
}

export interface ProductSales {
  name: string;
  units: number;
  revenue: number;
}

export interface DashboardInsightsDTO {
  range: DashboardRange;
  /** the first and last day of the period, "2026-09-20" to "2026-10-19" */
  from: string;
  to: string;
  kpis: {
    revenue: Compared;
    orders: Compared;
    averageOrder: Compared;
    newCustomers: Compared;
    itemsSold: number;
    /** customers (with an account) who bought in this period, and how many of them had bought before it */
    repeatBuyers: number;
    buyers: number;
    totalCustomers: number;
  };
  series: DayPoint[];
  payments: {
    online: { orders: number; revenue: number };
    cod: { orders: number; revenue: number };
    /** money given away: coupons, and the discount for paying online */
    couponDiscount: number;
    onlineDiscount: number;
  };
  coupons: { code: string; uses: number; discount: number }[];
  statuses: { status: string; orders: number }[];
  geo: {
    states: StateSales[];
    cities: CitySales[];
    /** orders whose state could not be told (typed by hand), so the map does not quietly lose them */
    unknownOrders: number;
  };
  topProducts: ProductSales[];
  /** index 0 = Sunday, in India time */
  weekdays: { orders: number; revenue: number }[];
  /** index 0 = midnight to 1am, in India time */
  hours: number[];
  checkouts: {
    started: number;
    ordered: number;
    /** left and not yet ordered */
    abandoned: number;
    abandonedValue: number;
    /** came back and ordered after a reminder */
    recovered: number;
    recoveredValue: number;
  };
  funnel: { productViews: number; addedToCart: number; orders: number };
  attention: {
    ordersToShip: number;
    awaitingPayment: number;
    customRequests: number;
    reviewsToApprove: number;
    lowStock: number;
    leftAtCheckout: number;
    productsMissingData: number;
  };
}
